import React from "react";
import { Reveal, SectionHeading } from "@/components/common/Reveal";
import { WHY_CARDS, PIPELINE_STATUSES, FAQS, BRAND } from "@/data";
import { useEnquiryForm } from "@/context/FormContext";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { motion } from "framer-motion";
import { Rocket, Building2, Landmark, MessageSquare, ArrowUpRight } from "lucide-react";

export function WhyFundAI() {
  return (
    <section className="relative py-24 md:py-36 border-t border-white/5" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionHeading label="Why FundAI" title="For businesses that need more than introductions." className="max-w-3xl" />
          <p className="mt-6 max-w-2xl text-base md:text-lg font-light text-zinc-400 leading-relaxed">FundAI is designed for businesses that require more than introductions and for strategic partners that value structured execution.</p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHY_CARDS.map((c, i) => (
            <Reveal key={c.n} delay={(i % 3) * 0.08}>
              <div className="card-hover h-full rounded-2xl border border-white/5 bg-[#0C0C10] p-7 hover:border-white/15" data-testid={`why-card-${i}`}>
                <span className="font-mono text-sm text-[#0057FF]">{c.n}</span>
                <h3 className="font-head text-lg font-medium mt-3">{c.title}</h3>
                <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function VenturePipeline() {
  return (
    <section className="relative py-24 md:py-36 border-t border-white/5" data-testid="pipeline-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionHeading label="FundAI Venture Pipeline" title="Our first group of AI ventures is currently under evaluation." className="max-w-3xl" />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12 rounded-2xl border border-white/10 bg-[#0A0A0E] p-6 md:p-8 font-mono" data-testid="pipeline-terminal">
            <div className="flex items-center gap-2 mb-6 text-xs text-zinc-600"><span className="h-2.5 w-2.5 rounded-full bg-[#0057FF] animate-pulse" /> PIPELINE STATUS — LIVE</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {PIPELINE_STATUSES.map((s, i) => (
                <div key={s} className="flex items-center justify-between rounded-lg border border-white/5 bg-[#0C0C10] px-4 py-3">
                  <span className="text-xs text-zinc-400">{s}</span>
                  <span className="text-[10px] text-zinc-700">{String(i + 1).padStart(2, "0")}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-[11px] text-zinc-600 leading-relaxed font-body">Company names, logos and portfolio status are published only after written company consent, an approved public description, compliance approval and an audit record. No confidential funding information is disclosed.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function About() {
  return (
    <section id="about" className="relative py-24 md:py-36 border-t border-white/5" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1fr_1fr] gap-12">
        <Reveal>
          <SectionHeading label="About FundAI" title="Building the intelligence-led economy." />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="space-y-6 text-base md:text-lg font-light text-zinc-400 leading-relaxed">
            <p>FundAI is a brand of Nexcore Ventures Private Limited focused on helping AI-enabled businesses progress from validation to commercial scale. The platform combines venture development, strategic-capital access, technology, marketing, workforce, manufacturing and distribution support.</p>
            <p>FundAI does not operate as a public investment fund or crowdfunding marketplace. Capital and financial products are provided only through the relevant lawful corporate, financial or provider channels.</p>
            <div className="grid grid-cols-2 gap-4 pt-4 font-mono text-xs text-zinc-500">
              <div><div className="text-zinc-600">Legal owner</div><div className="text-zinc-300 mt-1">{BRAND.owner}</div></div>
              <div><div className="text-zinc-600">CIN</div><div className="text-zinc-300 mt-1">{BRAND.cin}</div></div>
              <div><div className="text-zinc-600">GSTIN</div><div className="text-zinc-300 mt-1">{BRAND.gstin}</div></div>
              <div className="col-span-2"><div className="text-zinc-600">AMFI</div><div className="text-zinc-400 mt-1 leading-relaxed font-body">{BRAND.arn}</div></div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function FAQ() {
  return (
    <section id="faqs" className="relative py-24 md:py-36 border-t border-white/5" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Reveal><SectionHeading label="FAQs" title="Clear answers, conservatively stated." className="mb-12" /></Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/8" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-head text-base md:text-lg font-medium hover:no-underline hover:text-[#00D1FF] py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-400 leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

export function Contact() {
  const { openForm } = useEnquiryForm();
  const cards = [
    { icon: Rocket, title: "I am building an AI business", type: "founder" },
    { icon: Landmark, title: "I am exploring financial products", type: "investment" },
    { icon: Building2, title: "I represent a strategic organisation", type: "partner" },
    { icon: MessageSquare, title: "I have a general enquiry", type: "general" },
  ];
  return (
    <section id="contact" className="relative py-24 md:py-36 border-t border-white/5" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionHeading label="Start a Conversation" title="Select the nature of your enquiry." className="max-w-3xl" />
          <p className="mt-6 max-w-2xl text-base md:text-lg font-light text-zinc-400 leading-relaxed">Submit the relevant form and the FundAI team will contact you after reviewing your enquiry.</p>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.type} delay={i * 0.06}>
                <button onClick={() => openForm(c.type)} className="group w-full text-left h-full rounded-2xl border border-white/5 bg-[#0C0C10] p-7 card-hover hover:border-[#0057FF]/40 hover:-translate-y-1" data-testid={`contact-card-${c.type}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-white/5 mb-6"><Icon className="h-5 w-5 text-[#00D1FF]" /></div>
                  <h3 className="font-head text-lg font-medium">{c.title}</h3>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-zinc-500 group-hover:text-white transition-colors">Open form <ArrowUpRight className="h-4 w-4" /></span>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function FinalCTA() {
  const { openForm } = useEnquiryForm();
  return (
    <section className="relative py-28 md:py-44 border-t border-white/5 overflow-hidden" data-testid="final-cta-section">
      <div className="absolute inset-0 grid-lines opacity-30" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] glow-blue" />
      <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="font-head text-4xl md:text-6xl font-light tracking-tight text-metallic leading-[1.05]">
          Build what intelligence<br />makes possible.
        </motion.h2>
        <p className="mt-6 text-base md:text-lg font-light text-zinc-400 leading-relaxed max-w-2xl mx-auto">
          Whether you are building an AI-powered business, exploring approved financial products or contributing strategic capabilities, FundAI provides a structured place to begin.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
          <button onClick={() => openForm("founder")} className="bg-[#0057FF] hover:bg-[#0047D6] text-white rounded-full px-7 py-3.5 font-medium transition-colors active:scale-[0.98]" data-testid="final-apply-btn">Apply for Funding</button>
          <button onClick={() => openForm("investment")} className="border border-white/15 hover:border-white/40 text-white rounded-full px-7 py-3.5 font-medium transition-colors" data-testid="final-consult-btn">Request a Consultation</button>
          <button onClick={() => openForm("partner")} className="text-zinc-300 hover:text-white rounded-full px-5 py-3.5 font-medium transition-colors" data-testid="final-partner-btn">Become a Strategic Partner</button>
        </div>
      </div>
    </section>
  );
}
